import type { ErrorClassification, NormalizedLogRecord } from "./logs";

export type ResponseStoredFilter = "all" | "stored" | "not-stored";

export type FixedStatusFilter = "all" | "fixed" | "not-fixed";

export type SortDirection = "asc" | "desc";

export type SortableField = Extract<
  keyof NormalizedLogRecord,
  "createdAt" | "updatedAt" | "statusCode" | "route" | "userId" | "method"
>;

export const SORTABLE_FIELDS: readonly SortableField[] = [
  "createdAt",
  "updatedAt",
  "statusCode",
  "route",
  "userId",
  "method",
];

export const ALLOWED_PAGE_SIZES = [25, 50, 100, 250] as const;
export type AllowedPageSize = (typeof ALLOWED_PAGE_SIZES)[number];

export const HTTP_METHODS = [
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
  "HEAD",
  "OPTIONS",
] as const;
export type HttpMethod = (typeof HTTP_METHODS)[number];

export interface LogQueryParams {
  userIds?: string[];
  routes?: string[];
  methods?: string[];
  errorStatuses?: ErrorClassification[];
  statusCodes?: number[];
  responseStored?: ResponseStoredFilter;
  fixedStatus?: FixedStatusFilter;
  createdFrom?: Date;
  createdTo?: Date;
  updatedFrom?: Date;
  updatedTo?: Date;
  /** Inclusive minimum approximate response time (updatedAt − createdAt), in ms. */
  responseTimeMinMs?: number;
  /** Inclusive maximum approximate response time (updatedAt − createdAt), in ms. */
  responseTimeMaxMs?: number;
  search?: string;
  page?: number;
  pageSize?: number;
  cursor?: string;
  sortBy?: SortableField;
  sortDirection?: SortDirection;
  routeGroupingEnabled?: boolean;
  /**
   * Exact matching row count — required when jumping to a page near the end
   * so the service can size the (possibly partial) last page correctly.
   */
  totalCount?: number;
  /**
   * Optional override for how many DynamoDB pages may be evaluated while
   * filling one UI page. Used by lightweight callers (filter-options) to
   * keep Scan fallback from running for minutes.
   */
  maxEvaluatedPages?: number;
  /**
   * @internal Server-only: stop after this many matched records (tail remainder).
   */
  recordLimit?: number;
}

export interface LogSummary {
  totalRequests: number;
  totalErrors: number;
  errorRate: number;
  successCount: number;
  unknownRecords: number;
  malformedRecords: number;
  uniqueUsers: number;
  uniqueRoutes: number;
  storedResponses: number;
}

export interface QueryExecutionMetadata {
  selectedAccessPattern: string;
  selectedIndex: string | null;
  indexBackedFilters: string[];
  postQueryFilters: string[];
  evaluatedItemCount: number;
  returnedItemCount: number;
  evaluatedPages: number;
  resultMayBePartial: boolean;
}

export type PaginationAnchor = "start" | "end";

export interface LogQueryResult {
  records: NormalizedLogRecord[];
  hasMore: boolean;
  nextCursor: string | null;
  metadata: QueryExecutionMetadata;
  /** 1-based page that was returned when a page jump was requested. */
  page?: number;
  /** Cursors needed to reopen pages 2..page (or end-depths 2..depth). */
  cursorStack?: string[];
  /** Whether this page was reached by walking from the start or the end. */
  anchor?: PaginationAnchor;
}

export type TimeBucketGranularity = "hour" | "day" | "week";

export interface FilterOption {
  value: string;
  count: number;
}

export interface FilterOptionsResult {
  users: FilterOption[];
  routes: FilterOption[];
  methods: FilterOption[];
  statusCodes: FilterOption[];
  errorStatuses: FilterOption[];
  hasMoreUsers: boolean;
  hasMoreRoutes: boolean;
  /** Approximate item count for the whole table (DescribeTable). */
  totalCount: number;
  /** Matching records found while building interrelated filter options. */
  matchingCount: number;
  /** True when matchingCount may be lower than the true filtered total. */
  matchingCountIsPartial: boolean;
}
